import React, { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Icon } from "../../../components/Component";
import { Button, Card, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import { Badge, DatePicker, Drawer, Modal, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import SearchInput from "../../form-components/SearchComponent";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import { getDirtyFieldsOnly } from "../../../utils/use-form/GetDirtyFieldsOnly";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import dayjs from "dayjs";
import PhaseInDetailModal from "../../ProjectScheduleManagement/phases/PhaseInDetailModal";
import ActivityInDetailModal from "../../ProjectScheduleManagement/activity/ActivityInDetailModal";
import { RootState } from "../../../store/store";
import reportApis from "../../../api/reports";
import Export from "../../../components/export/Export";
import { resetPhaseInDetailModalSlice } from "../../../store/slices/projectManagement/phase-view/PhaseInDetailView";
import { resetActivityInDetailModalSlice } from "../../../store/slices/projectManagement/activity-view/ActivityInDetailView";
import { resetTaskListDetailsModalSlice } from "../../../store/slices/projectManagement/task-list-view/TaskListDetailModalSlice";
import { resetIssueInDetailModalSlice } from "../../../store/slices/projectManagement/issuee-view/IssueInDetailView";

const { RangePicker } = DatePicker;

type customFilters = {
  type?: "single_selection" | "multiple_selection";
  name: string;
  label: string;
  options: SelectOption[];
};

const Template1: FC<{
  moduleKey: string;
  columns: any;
  pageTitle: string;
  children?: React.ReactNode;
  pageDescription?: string;
  previousPath: {
    link: string;
    state?: any;
  };
  filters?: {
    defaultFilters?: {
      dateRange?: {
        title: string;
      };
    };
    customFilters?: customFilters[];
  };
  extra?: {
    derivedProjectStatus?: string;
    showOnlyTable?: boolean;
    taskOwnerId?: number;
  };
}> = ({
  moduleKey,
  columns,
  pageTitle,
  pageDescription,
  previousPath,
  filters,
  extra,
}) => {
  const { derivedProjectStatus } = extra || {};
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const { data: RootPageRenderState } = useSelector(
    (state: RootState) => state.RootPageRenderState
  );
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { id: projectId, projectName } = location.state || {};
  const dispatch = useDispatch();

  // Template1 still renders <PhaseInDetailModal /> and <ActivityInDetailModal />,
  // each of which is driven by its Redux slice's `open` flag. Task and issue
  // details have moved to their own full-page routes, so those modals are no
  // longer mounted here. If a user lands on a report screen while any of
  // these slices (phase / activity / task / issue) still has `open: true`
  // from a previous page, defensively reset them on mount so nothing pops
  // up unexpectedly and stale state from prior flows doesn't leak in.
  useEffect(() => {
    dispatch(resetPhaseInDetailModalSlice());
    dispatch(resetActivityInDetailModalSlice());
    dispatch(resetTaskListDetailsModalSlice());
    dispatch(resetIssueInDetailModalSlice());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const methods = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = methods;
  const [initialCall, setInitialCall] = useState(true);
  const [dataSource, setDataSource] = useState<any>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],

    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setSearchQuery] = useState(null);
  const [filtersModal, setFiltersModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<any>();
  const [activeFilters, setActiveFiltered] = useState<any>({}); // custom filters

  const selectFilter1 = watch("selectFilter1");
  const selectFilter2 = watch("selectFilter2");
  const selectFilter3 = watch("selectFilter3");

  const getExportFilters = () => {
    const {
      tag,
      owner,
      phaseStatus,
      activityStatus,
      group,
      workCategoryId,
      completion,
      UserId,
      customDateRange: filterDateRange,
    } = activeFilters;

    // Map filter names to export parameter names
    const filterMapping: Record<string, string> = {
      tag: "tagIds",
      UserId: "taskOwnerIds",
      phaseStatus: "status",
      activityStatus: "status",
      group: "baselineIds",
      workCategoryId: "workCategoryIds",
      completion: "completionStatus",
      customDateRange: "dateRange",
    };

    const exportFilters: any = {};

    // Add project ID
    if (projectId) {
      exportFilters.projectIds = projectId.toString();
    }

    if (filterDateRange && filterDateRange[0] && filterDateRange[1]) {
      exportFilters.startDate = dayjs(filterDateRange[0]).format("YYYY-MM-DD");
      exportFilters.endDate = dayjs(filterDateRange[1]).format("YYYY-MM-DD");
    }

    Object.keys(activeFilters).forEach((filterKey) => {
      if (filterKey === "customDateRange") return;

      const exportKey = filterMapping[filterKey] || filterKey;

      if (activeFilters[filterKey]) {
        if (Array.isArray(activeFilters[filterKey])) {
          exportFilters[exportKey] = activeFilters[filterKey].join(",");
        } else {
          exportFilters[exportKey] = activeFilters[filterKey].toString();
        }
      }
    });

    console.log("Export Filters:", exportFilters);
    return exportFilters;
  };

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
      } = params;
      let apiResponse: any = {};
      const {
        tag,
        owner,
        phaseStatus,
        activityStatus,
        group,
        workCategoryId,
        completion,
        UserId,
        customDateRange,
      } = activeFilters;
      switch (moduleKey) {
        case "/pms/task-reassignment-reports-list":
          //taskReassignment
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "taskReassignment",
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
          });
          break;
        case "/pms/task-aging-report":
          //taskAging
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "taskAging",
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
          });
          break;
        case "/pms/unassigned-task-report":
          //Call the api assign to the apiResponse
          break;
        case "/pms/checklist-adherence-by-user-report":
          //checklistAdherence
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "checklistAdherence",
            taskOwnerIds: UserId?.join(",") || null,
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
          });
          break;
        case "/pms/onhold-tasks-report":
          //onHoldTasksAndRootCause
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "onHoldTasksAndRootCause",
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
            taskOwnerIds: UserId?.join(",") || null,
          });

          break;

        case "/pms/approvals-action-delays-report":
          //approvalActionDelays
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "approvalActionDelays",
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
            taskOwnerIds: UserId?.join(",") || null,
          });

          break;

        case "/pms/tagwise-task-performance-report":
          //tagWiseTaskPerformance
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "tagWiseTaskPerformance",
            tagIds: tag?.join(",") || null,
          });
          break;
        case "/pms/statuswise-task-performance-report":
          //statusWiseTaskPerformance
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "statusWiseTaskPerformance",
            taskOwnerIds: UserId?.join(",") || null,
          });
          break;
        case "/pms/work-category-wise-task-performance-report":
          //workCategoryWiseTaskPerformance
          apiResponse = await reportApis.fetchPMSReports({
            page: current,
            projectIds: projectId,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "workCategoryWiseTaskPerformance",
            workCategoryIds: workCategoryId?.join(",") || null,
          });
          break;
        case "/pms/phase-level-progress-summary-report":
          // phaseLevelProgressSummaryReport
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "phaseLevelProgressSummaryReport",
            status: phaseStatus?.join(",") || null,
          });
          break;
        case "/pms/activity-level-progress-summary-report":
          // activityLevelProgressSummaryReport

          const activityApiParams: any = {
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "activityLevelProgressSummaryReport",
            status: activityStatus?.join(",") || null,
          };

          // Only add phaseIds if they exist (when navigating from Phase Level report)
          if (location.state?.phaseIds && location.state.phaseIds.length > 0) {
            activityApiParams.phaseIds = location.state.phaseIds.join(",");
          }
          apiResponse = await reportApis.fetchPMSReports(activityApiParams);
          break;
        case "/pms/task-completion-report":
          //taskCompletionReport
          const taskApiParams: any = {
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "taskCompletionReport",
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
            status: phaseStatus?.join(",") || null,
            taskOwnerIds: UserId?.join(",") || null,
          };

          // Only add activityIds if they exist (when navigating from Activity Level report)
          if (
            location.state?.activityIds &&
            location.state.activityIds.length > 0
          ) {
            taskApiParams.activityIds = location.state.activityIds.join(",");
          }

          apiResponse = await reportApis.fetchPMSReports(taskApiParams);
          break;
        case "/pms/task-issue-tracker":
          //taskAndIssueTracker
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "taskAndIssueTracker",
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
            status: phaseStatus?.join(",") || null,
            taskOwnerIds: UserId?.join(",") || null,
          });
          break;
        case "/pms/baseline-current-timeline-report":
          //baseLineVsCurrentTimeline
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "baseLineVsCurrentTimeline",
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
            status: phaseStatus?.join(",") || null,
            taskOwnerIds: UserId?.join(",") || null,
            baselineName: group?.join(",") || null,
          });
          break;
        case "/pms/dependency-violation-report":
          //dependencyVoilationReport
          apiResponse = await reportApis.fetchPMSReports({
            page: current,
            projectIds: projectId,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "dependencyVoilationReport",
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
            status: phaseStatus?.join(",") || null,
            taskOwnerIds: UserId?.join(",") || null,
            baselineName: group?.join(",") || null,
          });

          break;

        case "/pms/userwise-workload-report":
          //userWiseWorkloadReport
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "userWiseWorkloadReport",
            taskOwnerIds: UserId?.join(",") || null,
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
          });
          break;
        case "/pms/userwise-planned-actuals-report":
          //userWisePlannedVsActual
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "userWisePlannedVsActual",
            taskOwnerIds: UserId?.join(",") || null,
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
          });
          break;
        case "/pms/time-log-reports":
          //timeLogReport
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "timeLogReport",
            workCategoryIds: workCategoryId?.join(",") || null,
            taskOwnerIds: UserId?.join(",") || null,
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
          });

          break;

        case "/pms/time-log-analysis-report":
          //timeLoganalysisReport
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "taskWiseTimeLog",
            status: phaseStatus?.join(",") || null,
            taskOwnerIds: UserId?.join(",") || null,
          });

          break;

        case "/pms/petty-cash-inflow-outflow-report":
          //call the api assign to the apiResponse

          break;

        case "/pms/daily-progress-digest":
          //dailyProgressDigest
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "dailyProgressDigest",
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
          });

          break;

        case "/pms/task-wise-timelogs":
          //timeLogReport
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "taskWiseTimeLog",
            startDate:
              (customDateRange &&
                dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
              null,
            endDate:
              (customDateRange &&
                dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
              null,
            taskOwnerIds: UserId?.join(",") || null,
          });

          break;

        case "/pms/activity-wise-timelogs":
          //timeLogReport
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "activityWiseTimeLog",
            status: phaseStatus?.join(",") || null,
            taskOwnerIds: UserId?.join(",") || null,
          });

          break;

        case "/pms/phase-wise-timelogs":
          //timeLogReport
          apiResponse = await reportApis.fetchPMSReports({
            projectIds: projectId,
            page: current,
            pageSize: pageSize,
            searchQuery,
            direction,
            sortOrder,
            pageDiff,
            paginationId,
            reportName: "phaseWiseTimeLog",
            status: phaseStatus?.join(",") || null,
            taskOwnerIds: UserId?.join(",") || null,
          });

          break;

        default:
          alert("Module not found : " + moduleKey);
      }
      const { data, status } = apiResponse || {};
      if (status) {
        const { list, count } = data;
        setPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));
        if (list)
          setDataSource((prev: any) => (append ? [...prev, ...list] : list));
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };
  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };
    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      // if page size is changed
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1; // calculating new current page based on new page size
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current
            ? "F" // Forward
            : "B"; // Backward
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.paginationId // Last item ID for backward direction
            : dataSource[0]?.paginationId; // First item ID for forward direction
        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }
    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
    });
  };

  const refreshListView = (from?: string) => {
    try {
      console.log("CallFrom ", from);

      // Only reset pagination if we're doing a full reset
      if (from === "reset" || from === "clear all filters") {
        setPagination({
          current: 1,
          pageSize: 15,
          defaultCurrent: 1,
          defaultPageSize: 15,
          showSizeChanger: true,
          pageSizeOptions: ["15", "20", "30", "40", "50"],
          total: 0,
        });
      }
      fetchData({
        oldPage: 1,
        current: pagination.current.toString(),
        sortOrder: "DESC",
        pageSize: pagination.pageSize.toString(),
        direction: "P",
        pageDiff: 0,
        paginationId: null,
        searchQuery,
      });
    } catch (error) {}
  };
  useEffect(() => {
    // if (Object.keys(activeFilters).length > 0)
    refreshListView("filter");
  }, [activeFilters]);
  useEffect(() => {
    if (initialCall) {
      setInitialCall(false);
    } else refreshListView("RootPageRenderState change");
  }, [RootPageRenderState]);
  const refreshData = (overRideData?: any) => {
    fetchData({
      oldPage: 1,
      current: pagination.current.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: pagination.current - 1,
      paginationId: null,
      searchQuery,
      ...overRideData,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);
  const hasDateRangeFilter = filters?.defaultFilters?.dateRange ? true : false;
  const customFilters = filters?.customFilters;
  return (
    <>
      <PhaseInDetailModal />
      <ActivityInDetailModal />

      <Head title={pageTitle} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div
              className="card-inner card-inner-lg"
              style={{ minHeight: "87vh" }}
            >
              {/* Page Title with Project Name */}
              <div className="mb-3">
                <h5 style={{ marginBottom: "5px", fontWeight: "600" }}>
                  {projectName || pageTitle}
                </h5>
              </div>
              
              <FormProvider {...methods}>
                <form>
                  <div
                    className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
                    style={{ minHeight: "40px", border: "0px" }}
                  >
                    <div className="nk-ibx-head-actions w-45 ms-1">
                      <ul className="nk-ibx-head-tools g-1 m-0 p-0 w-100 ">
                        <div className={`w-100 m-0 `}>
                          <SearchInput
                            name="search"
                            placeholder="Search..."
                            width="100%"
                            prefixIcon={<SearchOutlined />}
                            onDebouncedSearch={async (value: any) => {
                              setSearchQuery(value);
                              setPagination({
                                ...pagination,
                                current: 1,
                              });
                              await fetchData({
                                oldPage: 1,
                                current: 1,
                                sortOrder: "DESC",
                                pageSize: pagination.pageSize.toString(),
                                direction: "P",
                                pageDiff: 0,
                                paginationId: null,
                                searchQuery: value,
                              });
                            }}
                            debounceDelay={300} // Adjust delay as needed
                          />
                        </div>
                      </ul>
                    </div>
                    <ul className="nk-ibx-head-tools g-1 m-0 p-0 ">
                      <li className="pt-0 pb-0">
                        <Export
                          data={{
                            sortOrder: "DESC",
                            pageSize: 100,
                            direction: "P",
                            pageDiff: 0,
                            searchQuery,
                            ...getExportFilters(),
                            selectFilter1,
                            selectFilter2,
                            selectFilter3,
                            // dateRange: dateRange && convertToDates(dateRange),
                            // customDateRange,
                          }}
                          moduleKey={moduleKey}
                          showAsButton={true}
                        />
                      </li>
                      <li className="pt-0 pb-0">
                        <Button
                          className="toggle"
                          color="primary"
                          size="sm"
                          onClick={() => {
                            navigate(
                              previousPath?.link || "/pms/project/reports",
                              {
                                state: previousPath?.state,
                              }
                            );
                          }}
                        >
                          <Icon name={"arrow-left"} />
                          <span>Back</span>
                        </Button>
                      </li>
                      <li>
                        <UncontrolledDropdown>
                          <DropdownToggle
                            tag="a"
                            className="dropdown-toggle btn btn-icon btn-trigger"
                            onClick={(ev) => {
                              refreshListView("refresh");
                            }}
                          >
                            <Icon name="redo"></Icon>
                          </DropdownToggle>
                        </UncontrolledDropdown>
                      </li>

                      <li className="me-n1">
                        <a
                          href="#filter"
                          onClick={(ev) => {
                            ev.preventDefault();
                            setFiltersModal(true);
                          }}
                          className={`btn-icon me-2`}
                        >
                          <Badge
                            color="geekblue"
                            size="small"
                            count={Object.keys(activeFilters)?.length || 0}
                          >
                            <Icon
                              style={{ fontSize: "20px" }}
                              name="filter"
                            ></Icon>
                          </Badge>
                        </a>
                      </li>
                    </ul>
                  </div>
                </form>
              </FormProvider>

              <div className="card-inner card-inner-lg p-0">
                <div className="content clearfix">
                  <div
                    style={{
                      width: screenSize - (SideNavCollapse ? 120 : 320) + "px",
                      margin: "10px auto",
                    }}
                  >
                    <SkeletonTable
                      loading={loading}
                      columns={columns as SkeletonTableColumnsType[]}
                    >
                      <Table
                        rowKey="uid"
                        columns={columns}
                        dataSource={dataSource}
                        pagination={pagination}
                        onChange={tableOnChange}
                        bordered
                        size="middle"
                        scroll={{
                          x: "max-content",
                        }}
                      />
                    </SkeletonTable>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Content>
      <Drawer
        maskClosable={false}
        title={
          <div className="d-flex justify-content-between align-items-center">
            <strong>Filters</strong>
            <>
              <div style={{ textAlign: "right" }}>
                <Button
                  onClick={() => {
                    methods.reset();
                    setActiveFiltered({});
                    setCustomDateRange(null);
                    // refreshListView("reset");
                  }}
                  color=""
                  className="m-3 mt-0 mb-0 ps-4 pe-4"
                  size="sm"
                >
                  <span style={{ color: "#0772f8" }}>Reset</span>
                </Button>
              </div>
            </>
          </div>
        }
        placement="right"
        width={450}
        onClose={() => {
          setFiltersModal(false);
        }}
        open={filtersModal}
        styles={{ body: { paddingBottom: 80 } }}
      >
        <div className="d-flex align-items-center flex-wrap w-100">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit((data) => {
                setFiltersModal(false);
                const dirtyData = getDirtyFieldsOnly(data, dirtyFields) || {};
                // Check if all filters are empty
                const isAllFiltersEmpty =
                  Object.keys(dirtyData).length === 0 &&
                  (!customDateRange ||
                    (customDateRange &&
                      !customDateRange[0] &&
                      !customDateRange[1]));
                if (isAllFiltersEmpty) {
                  // If all filters are empty, reset to initial state
                  setActiveFiltered({});
                  refreshListView("clear all filters");
                } else {
                  // Otherwise apply the filters
                  if (
                    customDateRange &&
                    customDateRange[0] &&
                    customDateRange[1]
                  ) {
                    dirtyData.customDateRange = customDateRange;
                  }
                  setActiveFiltered(dirtyData);
                }
              })}
              className="w-100"
            >
              {hasDateRangeFilter ? (
                <div key={"filters5"} className="mt-0 mb-2">
                  <label className="form-label">
                    {filters?.defaultFilters?.dateRange?.title}
                  </label>
                  <RangePicker
                    name={`dateRange`}
                    value={
                      customDateRange
                        ? [
                            dayjs(customDateRange[0].toDate()),
                            dayjs(customDateRange[1].toDate()),
                          ]
                        : null
                    }
                    onChange={(dates) => {
                      if (dates && dates[0] && dates[1]) {
                        setCustomDateRange([dates[0], dates[1]]);
                      } else {
                        setCustomDateRange(null);
                      }
                    }}
                    style={{ width: "100%" }}
                  />
                </div>
              ) : (
                <></>
              )}
              {customFilters?.map((filter: customFilters) => {
                return (
                  <div key={filter?.name} className="mb-2 mt-0">
                    {filter?.type === "single_selection" ? (
                      <FormSelect
                        name={filter?.name}
                        label={filter?.label}
                        placeholder={filter?.label}
                        options={filter?.options}
                      />
                    ) : (
                      <FormMultiSelect
                        name={filter?.name}
                        label={filter?.label}
                        placeholder={filter?.label}
                        options={filter?.options}
                      />
                    )}
                  </div>
                );
              })}
              <div className="actions clearfix">
                <ul>
                  <li>
                    <Button color="primary" size="sm" type="submit">
                      Find
                    </Button>
                  </li>
                  <li>
                    <Button
                      size="sm"
                      color="primary"
                      className="btn-dim btn btn-secondary"
                      type="button"
                      onClick={() => {
                        setFiltersModal(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </li>
                </ul>
              </div>
            </form>
          </FormProvider>
        </div>
      </Drawer>
    </>
  );
};
export default Template1;
